import { LeasingCarDto, SaleCarDto } from "api"

export interface PreviouslyViewedCar {
  // For sale cars: vehicleNumber, for leasing cars: `${manufacturerCode}-${modelCode}`
  identifier: string | number
  vehicleNumber?: number // Only for sale cars
  manufacturerCode?: number // Only for leasing cars
  modelCode?: number // Only for leasing cars
  name: string
  image: string
  icon?: string
  chips: string[]
  price: string
  viewedAt: number // timestamp
  classification?: number
  type: "Sale" | "Leasing"
  carData?: SaleCarDto | LeasingCarDto // Full car data for comparison
}

export function transformCarDataForPreviouslyViewed({
  carData,
  brandIcon,
  brands,
}: {
  carData: SaleCarDto
  brands?: {
    image: string
    title: string
    logo: string
    url: string
  }[]
  brandIcon?: string
}): PreviouslyViewedCar {
  // Extract chips based on available data
  const chips: string[] = []
  // 1. Hand status - convert from enum to Hebrew
  const handMap: Record<string, string> = {
    None: "חדש",
    First: "יד ראשונה",
    Second: "יד שנייה",
    Third: "יד שלישית",
    Fourth: "יד רביעית",
    FifthAndAbove: "יד חמישית ומעלה",
  }

  if (carData.hand && carData.hand !== "None") {
    chips.push(handMap[carData.hand] || carData.hand)
  } else if (carData.carClassification === "NewCar") {
    chips.push("חדש")
  }

  // 2. Mileage
  if (carData.mileage && carData.mileage > 0) {
    chips.push(`${carData.mileage.toLocaleString()} ק״מ`)
  } else if (carData.carClassification === "NewCar") {
    chips.push("0 ק״מ")
  }

  // 3. Drive type (fuel type)
  if (carData.driveTypeDescription) {
    chips.push(carData.driveTypeDescription)
  }

  // 4. Transmission type
  if (carData.transmissionTypeDescription) {
    chips.push(carData.transmissionTypeDescription)
  }

  // Ensure we have exactly 4 chips, pad with additional info if needed
  while (chips.length < 4) {
    if (chips.length === 0 && carData.manufacturerYear) {
      chips.push(`${carData.manufacturerYear}`)
    } else if (chips.length === 1 && carData.colorDescription) {
      chips.push(carData.colorDescription)
    } else if (chips.length === 2 && carData.engineCapacity) {
      chips.push(`${carData.engineCapacity}cc`)
    } else if (chips.length === 3 && carData.horsePower) {
      chips.push(`${carData.horsePower} כ״ס`)
    } else {
      break
    }
  }

  // Take only first 4 chips
  const finalChips = chips.slice(0, 4)
  const brand = brands?.find(
    (brand) => brand.title === carData.carZone?.name?.makeNameEn,
  )
  const mainImage = carData.detailedImages?.images?.find(
    (el) => el.label === "SRP",
  )

  return {
    identifier: carData.vehicleNumber,
    vehicleNumber: carData.vehicleNumber,
    name: carData.modelName || carData.carZone?.name?.modelNameEn || "",
    classification:
      carData.vehicleSingleValuedClassification[0]
        ?.vehicleSingleValuedClassificationCode,
    image: mainImage?.url || carData.mainImage, // fallback to dummy image
    icon: brandIcon || brand?.logo, // TODO: Add manufacturer icon mapping
    chips: finalChips,
    price: carData.advertisedPrice || carData.discountedPrice.toString() || "0",
    viewedAt: Date.now(),
    type: "Sale" as const,
    carData, // Include full car data for comparison
  }
}

export function transformLeasingCarDataForPreviouslyViewed({
  carData,
  brandIcon,
  brands,
}: {
  carData: LeasingCarDto
  brands?: {
    image: string
    title: string
    logo: string
    url: string
  }[]
  brandIcon?: string
}): PreviouslyViewedCar {
  // Extract chips based on available data for leasing cars
  const chips: string[] = []

  // 1. Always show "ליסינג" for leasing cars
  chips.push("ליסינג")

  // 2. Engine capacity
  if (carData.engineCapacity) {
    chips.push(`${carData.engineCapacity}cc`)
  }

  // 3. Add marketing features if available
  if (carData.marketingFeatures && carData.marketingFeatures.length > 0) {
    chips.push(carData.marketingFeatures[0])
  }

  // 4. Add manufacturer name if chips are still needed
  if (chips.length < 4 && carData.manufacturerName) {
    chips.push(carData.manufacturerName)
  }

  // Ensure we have exactly 4 chips, pad with additional info if needed
  while (chips.length < 4) {
    if (chips.length === 1 && carData.modelName) {
      chips.push(carData.modelName)
    } else if (chips.length === 2 && carData.carZone?.modelYear) {
      chips.push(`${carData.carZone.modelYear}`)
    } else if (chips.length === 3 && carData.basePackageDescription) {
      chips.push(carData.basePackageDescription)
    } else {
      break
    }
  }

  // Take only first 4 chips
  const finalChips = chips.slice(0, 4)
  const brand = brands?.find(
    (brand) => brand.title === carData.carZone?.name?.makeNameEn,
  )
  const mainImage = carData.detailedImages?.images?.find(
    (el) => el.label === "SRP",
  )

  return {
    identifier: `${carData.manufacturerCode}-${carData.modelCode}`,
    manufacturerCode: carData.manufacturerCode,
    modelCode: carData.modelCode,
    name: carData.modelName || "",
    classification:
      carData.vehicleSingleValuedClassification[0]
        ?.vehicleSingleValuedClassificationCode,
    image: mainImage?.url || carData.mainImage,
    icon: brandIcon || brand?.logo,
    chips: finalChips,
    price: carData.monthlyPaymentPriceIncludingVat?.toString() || "0",
    viewedAt: Date.now(),
    type: "Leasing" as const,
    carData, // Include full car data for comparison
  }
}
